import AdminShell from './AdminShell';
import { validNumberQuestion, validFreeQuestion } from '../../lib/text-answers';
/**
 * Редактор вопросов квиза
 */
import { useState, useEffect } from 'react';
import { getQuiz, updateQuiz, uploadFile, Quiz, QuizQuestion } from '../../lib/admin-api';

type Zone = NonNullable<QuizQuestion['zones']>[number];

/** Редактор зон «клик по фото»: прямоугольник — два нажатия (углы), многоугольник — нажатия по точкам и «Замкнуть».
 *  Координаты в % от картинки, как их ждёт плеер. */
function HotspotEditor({ question, courseId, onChange }: { question: QuizQuestion; courseId: string; onChange: (q: QuizQuestion) => void }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tool, setTool] = useState<'rect' | 'poly'>('rect');
  const [draft, setDraft] = useState<number[]>([]); // накопленные точки [x1,y1,x2,y2,...]
  const zones = question.zones || [];
  const zoneId = () => `z${Date.now().toString(36)}${Math.floor(Math.random() * 100)}`;

  const upload = async (file: File | null) => {
    if (!file) return;
    setUploading(true); setError(null);
    try {
      const { url } = await uploadFile(file, { courseId, kind: 'image' });
      onChange({ ...question, image: { url, alt: question.image?.alt || '' } });
    } catch (e) { setError('Фото не загрузилось: ' + String((e as Error).message || e)); }
    finally { setUploading(false); }
  };
  const tap = (e: React.MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.round(((e.clientX - r.left) / r.width) * 1000) / 10;
    const y = Math.round(((e.clientY - r.top) / r.height) * 1000) / 10;
    const pts = [...draft, x, y];
    if (tool === 'rect' && pts.length === 4) {
      const [x1, y1, x2, y2] = pts;
      const z: Zone = { id: zoneId(), shape: 'rect', coords: [Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1)], correct: zones.length === 0, label: '' };
      onChange({ ...question, zones: [...zones, z] }); setDraft([]);
    } else setDraft(pts);
  };
  const closePoly = () => {
    if (draft.length < 6) return;
    const z: Zone = { id: zoneId(), shape: 'poly', coords: draft, correct: zones.length === 0, label: '' };
    onChange({ ...question, zones: [...zones, z] }); setDraft([]);
  };
  const setZone = (i: number, patch: Partial<Zone>) => onChange({ ...question, zones: zones.map((z, j) => (j === i ? { ...z, ...patch } : z)) });
  const delZone = (i: number) => onChange({ ...question, zones: zones.filter((_, j) => j !== i) });
  const problems: string[] = [];
  if (!question.image?.url) problems.push('нет фото');
  if (question.type === 'labels') {
    if (zones.filter(z => z.label?.trim()).length < 2) problems.push('нужны две зоны с подписью');
    if (zones.some(z => !z.label?.trim())) problems.push('подпишите каждую зону');
  } else if (!zones.some((z) => z.correct)) problems.push('нет верной зоны');

  return (
    <div className="space-y-2 rounded-lg border border-[#E1E5E3] p-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <label className="cursor-pointer rounded bg-white border border-[#E1E5E3] px-2 py-1 hover:bg-[#E5F2EF]">
          {uploading ? 'Загружаю…' : question.image?.url ? 'Заменить фото' : '📷 Загрузить фото'}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0] || null)} />
        </label>
        <span className="text-[#626B73]">Инструмент:</span>
        <button type="button" onClick={() => { setTool('rect'); setDraft([]); }} className={`rounded px-2 py-1 ${tool === 'rect' ? 'bg-[#0F766E] text-white' : 'bg-white border border-[#E1E5E3]'}`}>прямоугольник (2 нажатия)</button>
        <button type="button" onClick={() => { setTool('poly'); setDraft([]); }} className={`rounded px-2 py-1 ${tool === 'poly' ? 'bg-[#0F766E] text-white' : 'bg-white border border-[#E1E5E3]'}`}>многоугольник (точки)</button>
        {tool === 'poly' && draft.length >= 6 && <button type="button" onClick={closePoly} className="rounded bg-[#0F766E] text-white px-2 py-1">Замкнуть ({draft.length / 2} т.)</button>}
        {draft.length > 0 && <button type="button" onClick={() => setDraft([])} className="rounded bg-white border border-[#E1E5E3] px-2 py-1">сбросить</button>}
      </div>
      {error && <p className="text-xs text-[#DC2626]">{error}</p>}
      {question.image?.url ? (
        <div className="relative w-full cursor-crosshair overflow-hidden rounded border border-[#E1E5E3] bg-[#F7F7F4]/95" onClick={tap}>
          <img src={question.image.url} alt="" className="block w-full select-none" draggable={false} />
          <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            {zones.map((z, i) => {
              const stroke = question.type === 'labels' ? '#0F766E' : z.correct ? '#16a34a' : '#dc2626'; const fill = question.type === 'labels' ? 'rgba(15,118,110,0.18)' : z.correct ? 'rgba(22,163,74,0.25)' : 'rgba(220,38,38,0.18)';
              if (z.shape === 'rect') return <g key={z.id}><rect x={z.coords[0]} y={z.coords[1]} width={z.coords[2]} height={z.coords[3]} fill={fill} stroke={stroke} strokeWidth={0.6} vectorEffect="non-scaling-stroke" /><text x={z.coords[0] + 1} y={z.coords[1] + 4} fontSize={4} fill="#252A2D" stroke="#FFFFFF" strokeWidth={0.3} paintOrder="stroke">{i + 1}</text></g>;
              const pts: string[] = []; for (let k = 0; k < z.coords.length; k += 2) pts.push(`${z.coords[k]},${z.coords[k + 1]}`);
              return <g key={z.id}><polygon points={pts.join(' ')} fill={fill} stroke={stroke} strokeWidth={0.6} vectorEffect="non-scaling-stroke" /><text x={z.coords[0] + 1} y={z.coords[1] + 4} fontSize={4} fill="#252A2D" stroke="#FFFFFF" strokeWidth={0.3} paintOrder="stroke">{i + 1}</text></g>;
            })}
            {draft.length >= 2 && draft.map((_, k) => (k % 2 === 0 ? <circle key={k} cx={draft[k]} cy={draft[k + 1]} r={0.9} fill="#facc15" /> : null))}
            {tool === 'poly' && draft.length >= 4 && <polyline points={Array.from({ length: draft.length / 2 }, (_, k) => `${draft[2 * k]},${draft[2 * k + 1]}`).join(' ')} fill="none" stroke="#facc15" strokeWidth={0.5} vectorEffect="non-scaling-stroke" />}
          </svg>
        </div>
      ) : (
        <p className="text-xs text-[#626B73]">Сначала загрузите фото, потом обведите зоны. Фото только настоящие.</p>
      )}
      <div className="space-y-1">
        {zones.map((z, i) => (
          <div key={z.id} className="flex flex-wrap items-center gap-2 text-xs">
            <span className="w-5 text-[#626B73]">{i + 1}</span>
            {question.type !== 'labels' && <button type="button" onClick={() => setZone(i, { correct: !z.correct })} className={`rounded px-2 py-1 ${z.correct ? 'bg-[#0F766E] text-white' : 'bg-red-50 text-[#DC2626]'}`}>{z.correct ? 'верная' : 'ловушка'}</button>}
            <input required={question.type === 'labels'} maxLength={question.type === 'labels' ? 80 : undefined} value={z.label || ''} onChange={(e) => setZone(i, { label: e.target.value })} placeholder="подпись (что это)" className="w-32 rounded bg-white border border-[#E1E5E3] px-2 py-1" />
            <input value={z.explanation || ''} onChange={(e) => setZone(i, { explanation: e.target.value })} placeholder="пояснение: почему верно / почему нет" className="min-w-0 w-full sm:w-auto flex-1 rounded bg-white border border-[#E1E5E3] px-2 py-1" />
            <button type="button" onClick={() => delZone(i)} className="text-[#DC2626]">✕</button>
          </div>
        ))}
      </div>
      {problems.length > 0 && <p className="text-xs text-amber-700">Не готово: {problems.join(', ')}. Проверка курса такое не пропустит.</p>}
    </div>
  );
}

/** Линия задаётся последовательными нажатиями по фото. */
function LineEditor({ question, courseId, onChange }: { question: QuizQuestion; courseId: string; onChange: (q: QuizQuestion) => void }) {
  const [draft, setDraft] = useState<number[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lines = question.lines || [];
  const upload = async (file: File | null) => {
    if (!file) return;
    setUploading(true); setError(null);
    try {
      const { url } = await uploadFile(file, { courseId, kind: 'image' });
      onChange({ ...question, image: { url, alt: question.image?.alt || '' } });
      setDraft([]);
    } catch (e) { setError('Фото не загрузилось: ' + String((e as Error).message || e)); }
    finally { setUploading(false); }
  };
  const finish = () => {
    if (draft.length < 4 || lines.length >= 6 || uploading) return;
    onChange({ ...question, lines: [...lines, {
      id: `line-${crypto.randomUUID()}`, points: [...draft], correct: !lines.some(line => line.correct),
    }] });
    setDraft([]);
  };
  const problems: string[] = [];
  if (!question.image?.url) problems.push('нет фото');
  if (lines.length < 2 || lines.length > 6) problems.push('нужны 2–6 линий');
  if (lines.filter(line => line.correct).length !== 1) problems.push('нужна ровно одна верная линия');
  if (lines.some(line => line.points.length < 4 || line.points.length % 2 !== 0)) problems.push('в каждой линии нужны минимум две точки');
  if (draft.length) problems.push('завершите или отмените текущую линию');
  const svgPoints = (points: number[]) => Array.from({ length: points.length / 2 }, (_, i) => `${points[2 * i]},${points[2 * i + 1]}`).join(' ');
  return <div className="space-y-3">
    <label className="block text-xs text-[#626B73]">
      {uploading ? 'Загрузка…' : 'Загрузить фото'}
      <input type="file" accept="image/*" disabled={uploading} onChange={e => { void upload(e.target.files?.[0] || null); e.target.value = ''; }} className="block mt-1" />
    </label>
    {error && <p role="alert" className="text-xs text-[#DC2626]">{error}</p>}
    <input value={question.image?.alt || ''} onChange={e => onChange({ ...question, image: { url: question.image?.url || '', alt: e.target.value } })}
      placeholder="Описание фото" aria-label="Описание фото" className="w-full rounded bg-white border border-[#E1E5E3] px-3 py-2 text-sm" />
    {question.image?.url && <div className="relative overflow-hidden rounded-xl cursor-crosshair" onClick={e => {
      if (uploading || lines.length >= 6) return;
      const r = e.currentTarget.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const x = Math.max(0, Math.min(100, (e.clientX - r.left) / r.width * 100));
      const y = Math.max(0, Math.min(100, (e.clientY - r.top) / r.height * 100));
      setDraft(previous => [...previous, x, y]);
    }}>
      <img src={question.image.url} alt={question.image.alt || ''} draggable={false} className="block h-auto w-full" />
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="pointer-events-none absolute inset-0 h-full w-full">
        {lines.map((line, i) => <g key={line.id}>
          <polyline points={svgPoints(line.points)} fill="none" stroke={line.correct ? '#22c55e' : '#0F766E'} strokeWidth={2} vectorEffect="non-scaling-stroke" />
          <circle cx={line.points[0]} cy={line.points[1]} r={2.5} fill="#0F766E" />
          <text x={line.points[0]} y={line.points[1]} textAnchor="middle" dominantBaseline="central" fontSize={3} fill="white">{String.fromCharCode(65 + i)}</text>
        </g>)}
        <polyline points={svgPoints(draft)} fill="none" stroke="#facc15" strokeWidth={2} vectorEffect="non-scaling-stroke" />
        {Array.from({ length: draft.length / 2 }, (_, i) => <circle key={i} cx={draft[2 * i]} cy={draft[2 * i + 1]} r={0.8} fill="#facc15" />)}
      </svg>
    </div>}
    <p className="text-xs text-[#626B73]">Нажимайте по фото, чтобы задать точки линии, затем завершите её. Максимум 6 линий.</p>
    <div className="flex flex-wrap gap-2 text-xs">
      <button type="button" disabled={uploading || draft.length < 4 || lines.length >= 6} onClick={finish} className="rounded bg-teal-700 px-3 py-2 disabled:opacity-40">Завершить линию</button>
      <button type="button" disabled={!draft.length} onClick={() => setDraft(previous => previous.slice(0, -2))} className="rounded bg-white border border-[#E1E5E3] px-3 py-2 disabled:opacity-40">Убрать точку</button>
      <button type="button" disabled={!draft.length} onClick={() => setDraft([])} className="rounded bg-white border border-[#E1E5E3] px-3 py-2 disabled:opacity-40">Отменить линию</button>
    </div>
    {lines.map((line, i) => <div key={line.id} className="flex flex-wrap items-center gap-2 text-xs">
      <span>{String.fromCharCode(65 + i)}</span>
      <input value={line.label || ''} aria-label={`Подпись линии ${i + 1}`} placeholder="Подпись" className="rounded bg-white border border-[#E1E5E3] px-2 py-2"
        onChange={e => onChange({ ...question, lines: lines.map(item => item.id === line.id ? { ...item, label: e.target.value } : item) })} />
      <button type="button" aria-pressed={line.correct} onClick={() => onChange({ ...question, lines: lines.map(item => ({ ...item, correct: item.id === line.id })) })}
        className={`rounded px-2 py-2 ${line.correct ? 'bg-[#0F766E] text-white' : 'bg-white border border-[#E1E5E3]'}`}>{line.correct ? '✓ Верная' : 'Сделать верной'}</button>
      <input value={line.explanation || ''} aria-label={`Пояснение линии ${i + 1}`} placeholder="Пояснение линии" className="min-w-0 w-full sm:w-auto flex-1 rounded bg-white border border-[#E1E5E3] px-2 py-2"
        onChange={e => onChange({ ...question, lines: lines.map(item => item.id === line.id ? { ...item, explanation: e.target.value } : item) })} />
      <button type="button" onClick={() => {
        const remaining = lines.filter(item => item.id !== line.id);
        onChange({ ...question, lines: remaining.map((item, j) => line.correct ? { ...item, correct: j === 0 } : item) });
      }} className="text-[#DC2626]">Удалить</button>
    </div>)}
    {problems.length > 0 && <p className="text-xs text-amber-700">Не готово: {problems.join(', ')}.</p>}
  </div>;
}

interface QuizEditorProps {
  quizId: string;
  onClose: () => void;
}

function CategoriesEditor({ question, onChange }: { question: QuizQuestion; onChange: (q: QuizQuestion) => void }) {
  const categories = question.categories || [];
  const items = question.items || [];
  const validId = (id: string) => typeof id === 'string' && !!id.trim() && id.length <= 64;
  const ready = categories.length >= 2 && categories.length <= 4
    && categories.every(c => validId(c.id) && typeof c.name === 'string' && !!c.name.trim())
    && new Set(categories.map(c => c.id)).size === categories.length
    && items.length >= 4 && items.length <= 12
    && items.every(item => validId(item.id) && typeof item.text === 'string' && !!item.text.trim() && categories.some(c => c.id === item.category))
    && new Set(items.map(item => item.id)).size === items.length;
  return <div className="space-y-3">
    <label className="block text-xs text-[#626B73]">Категории — по одной на строку (2–4)
      <textarea value={categories.map(c => c.name).join('\n')} onChange={e => {
        const used = new Set(categories.map(c => c.id));
        onChange({ ...question, categories: e.target.value.split('\n').map((name, i) => {
          if (categories[i]) return { ...categories[i], name };
          let n = i + 1;
          while (used.has(`category${n}`)) n++;
          const id = `category${n}`;
          used.add(id);
          return { id, name };
        }) });
      }} className="mt-1 h-24 w-full rounded bg-white border border-[#E1E5E3] px-3 py-2 text-sm" />
    </label>
    <p className="text-xs text-[#626B73]">Предметы (4–12): текст и верная категория</p>
    {items.map((item, index) => <div key={item.id} className="flex flex-wrap gap-2">
      <input aria-label={`Предмет ${index + 1}`} value={item.text} onChange={e => onChange({ ...question,
        items: items.map((it, i) => i === index ? { ...it, text: e.target.value } : it),
      })} className="min-w-0 flex-1 rounded bg-white border border-[#E1E5E3] px-3 py-2 text-sm" />
      <select aria-label={`Категория предмета ${index + 1}`} value={item.category} onChange={e => onChange({ ...question,
        items: items.map((it, i) => i === index ? { ...it, category: e.target.value } : it),
      })} className="max-w-full rounded bg-white border border-[#E1E5E3] px-2 py-1 text-sm">
        <option value="">Выберите категорию</option>
        {item.category && !categories.some(c => c.id === item.category) && <option value={item.category}>Категория удалена</option>}
        {categories.map(c => <option key={c.id} value={c.id}>{c.name || 'Без названия'}</option>)}
      </select>
      <button type="button" aria-label={`Удалить предмет ${index + 1}`} onClick={() => onChange({ ...question, items: items.filter((_, i) => i !== index) })} className="text-[#DC2626] px-2">×</button>
    </div>)}
    <button type="button" disabled={items.length >= 12} onClick={() => {
      let n = items.length + 1;
      while (items.some(item => item.id === `item${n}`)) n++;
      onChange({ ...question, items: [...items, { id: `item${n}`, text: '', category: '' }] });
    }} className="rounded bg-white border border-[#E1E5E3] px-3 py-2 text-sm disabled:opacity-40">+ Предмет</button>
    {!ready && <p className="text-xs text-amber-700">Не готово: нужны 2–4 категории с названиями, 4–12 предметов с текстом и категорией из списка; id должны быть уникальными, непустыми и не длиннее 64 символов.</p>}
  </div>;
}

function QuestionEditor({
  question,
  index,
  courseId,
  onChange,
  onDelete,
}: {
  question: QuizQuestion;
  index: number;
  courseId: string;
  onChange: (q: QuizQuestion) => void;
  onDelete: () => void;
}) {
  const isHotspot = question.type === 'hotspot';
  const isLabels = question.type === 'labels';
  const isLine = question.type === 'line';
  const isOrder = question.type === 'order';
  const isNumber = question.type === 'number';
  const isFree = question.type === 'free';
  const isCategories = question.type === 'categories';
  return (
    <div className="bg-[#F7F7F4] rounded-lg p-3 space-y-3">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <span className="text-sm font-medium text-[#626B73]">Вопрос {index + 1}</span>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={question.type || 'single'}
            onChange={e => {
              const type = e.target.value as NonNullable<QuizQuestion['type']>;
              onChange(type === 'number' || type === 'free'
                ? { ...question, type, options: [], correctIndex: -1 }
                : type === 'categories'
                ? { ...question, type, options: [], correctIndex: -1, categories: question.categories || [], items: question.items || [] }
                : type === 'line'
                ? { ...question, type, options: [], correctIndex: -1, lines: question.lines || [] }
                : type === 'order'
                ? { ...question, type, options: [], correctIndex: -1, steps: question.steps || [] }
                : type === 'hotspot' || type === 'labels'
                ? { ...question, type, options: question.options?.length ? question.options : [], correctIndex: -1, mode: question.mode || 'one', zones: question.zones || [] }
                : { ...question, type, options: question.options?.length >= 2 ? question.options : ['', ''], correctIndex: Math.max(0, question.correctIndex) });
            }}
            className="bg-white border border-[#E1E5E3] rounded px-2 py-1 text-xs"
            title="Тип задания"
          >
            <option value="number">Число</option>
            <option value="free">Свободный ответ</option>
            <option value="single">Выбор ответа</option>
            <option value="hotspot">Клик по фото</option>
            <option value="labels">Подписи на фото</option>
            <option value="line">Выбор линии</option>
            <option value="categories">По категориям</option>
            <option value="order">Порядок действий</option>
          </select>
          <input
            value={question.skill || ''}
            onChange={e => onChange({ ...question, skill: e.target.value })}
            className="w-28 bg-white border border-[#E1E5E3] rounded px-2 py-1 text-xs"
            placeholder="навык"
            title="Какой навык проверяет (id раздела или название)"
          />
          <label className="flex items-center gap-1 text-xs text-[#626B73]" title="Тема не сдана, пока этот вопрос не отвечен верно">
            <input type="checkbox" checked={!!question.critical} onChange={e => onChange({ ...question, critical: e.target.checked })} />
            критичный
          </label>
          <select
            value={question.difficulty || 'medium'}
            onChange={e => onChange({ ...question, difficulty: e.target.value })}
            className="bg-white border border-[#E1E5E3] rounded px-2 py-1 text-xs"
          >
            <option value="easy">Лёгкий</option>
            <option value="medium">Средний</option>
            <option value="hard">Сложный</option>
          </select>
          <button onClick={onDelete} className="text-[#DC2626] hover:text-[#DC2626] text-sm">🗑</button>
        </div>
      </div>

      <textarea
        value={question.text}
        onChange={e => onChange({ ...question, text: e.target.value })}
        className="w-full bg-white border border-[#E1E5E3] rounded px-3 py-2 text-sm h-16"
        placeholder="Текст вопроса..."
      />

      {isHotspot && <label className="block text-xs text-[#626B73]">
        Режим
        <select value={question.mode || 'one'} onChange={e => onChange({ ...question, mode: e.target.value as 'one' | 'all' })}
          className="ml-2 rounded bg-white border border-[#E1E5E3] px-2 py-1">
          <option value="one">Одна зона</option>
          <option value="all">Все зоны</option>
        </select>
        {question.mode === 'all' && (question.zones || []).filter(z => z.correct === true).length < 2
          && <span className="ml-2 text-amber-700">Не готово: нужны минимум две верные зоны.</span>}
      </label>}

      {isNumber && <div className="space-y-2">
        {([['answer', 'Верное число'], ['tolerance', 'Допуск (абс.)'], ['tolerancePercent', 'Допуск (%)']] as const).map(([field, label]) =>
          <label key={field} className="block text-xs text-[#626B73]">{label}
            <input type="number" step="any" min={field === 'answer' ? undefined : 0} value={question[field] ?? ''}
              onChange={e => onChange({ ...question, [field]: e.target.value === '' ? undefined : e.target.valueAsNumber })}
              className="mt-1 w-full rounded bg-white border border-[#E1E5E3] px-3 py-2" />
          </label>)}
        <label className="block text-xs text-[#626B73]">Единицы
          <input value={question.unit ?? ''} onChange={e => onChange({ ...question, unit: e.target.value })}
            className="mt-1 w-full rounded bg-white border border-[#E1E5E3] px-3 py-2" />
        </label>
        {!validNumberQuestion(question) && <p className="text-xs text-amber-700">Не готово: нужны число и неотрицательные допуски.</p>}
      </div>}
      {isFree && <div className="space-y-2">
        <label className="block text-xs text-[#626B73]">Верные ответы (по одному на строку)
          <textarea value={(question.accepted ?? []).join('\n')}
            onChange={e => onChange({ ...question, accepted: e.target.value === '' ? [] : e.target.value.split('\n') })}
            className="mt-1 w-full rounded bg-white border border-[#E1E5E3] px-3 py-2" />
        </label>
        <label className="block text-xs text-[#626B73]">Обязательные слова (через запятую)
          <input value={(question.keywords ?? []).join(',')}
            onChange={e => onChange({ ...question, keywords: e.target.value === '' ? [] : e.target.value.split(',') })}
            className="mt-1 w-full rounded bg-white border border-[#E1E5E3] px-3 py-2" />
        </label>
        <label className="block text-xs text-[#626B73]">Максимальная длина
          <input type="number" min={1} max={300} step={1} value={question.maxLength ?? 200}
            onChange={e => onChange({ ...question, maxLength: e.target.value === '' ? undefined : e.target.valueAsNumber })}
            className="mt-1 w-full rounded bg-white border border-[#E1E5E3] px-3 py-2" />
        </label>
        {!validFreeQuestion(question) && <p className="text-xs text-amber-700">Не готово: нужны 1–10 вариантов или ключевые слова без пустых строк; длина — от 1 до 300.</p>}
      </div>}
      {isCategories && <CategoriesEditor question={question} onChange={onChange} />}

      {isLine && <LineEditor question={question} courseId={courseId} onChange={onChange} />}

      {(isHotspot || isLabels) && <HotspotEditor question={question} courseId={courseId} onChange={onChange} />}

      {isLabels && <label className="block text-xs text-[#626B73]">
        Лишние подписи (через запятую)
        <input
          value={(question.distractors || []).join(',')}
          onChange={e => onChange({ ...question, distractors: e.target.value.split(',') })}
          onBlur={e => onChange({ ...question, distractors: e.target.value.split(',').map(label => label.trim()).filter(Boolean) })}
          className="mt-1 w-full rounded bg-white border border-[#E1E5E3] px-3 py-2 text-sm"
        />
      </label>}

      {isOrder && <label className="block text-xs text-[#626B73]">
        Шаги — по одному шагу на строку, в правильном порядке
        <textarea
          value={(question.steps || []).map(step => step.text).join('\n')}
          onChange={e => onChange({ ...question, steps: e.target.value.split('\n').map((text, i) => ({ id: `step${i + 1}`, text })) })}
          onBlur={e => onChange({ ...question, steps: e.target.value.split('\n').map(text => text.trim()).filter(Boolean).map((text, i) => ({ id: `step${i + 1}`, text })) })}
          className="mt-1 h-40 w-full rounded bg-white border border-[#E1E5E3] px-3 py-2 text-sm"
        />
        {(question.steps || []).filter(step => step.text.trim()).length < 3 && <span className="text-amber-700">Не готово: нужны минимум три шага.</span>}
        {(question.steps || []).length > 30 && <span className="text-amber-700">Не готово: максимум 30 шагов.</span>}
      </label>}

      {!isNumber && !isFree && !isHotspot && !isLabels && !isOrder && !isLine && !isCategories && <div className="space-y-2">
        {question.options.map((opt, i) => (
          <div key={i} className="flex items-center gap-2">
            <button
              onClick={() => onChange({ ...question, correctIndex: i })}
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0 ${
                question.correctIndex === i
                  ? 'bg-[#0F766E] text-white'
                  : 'bg-white border border-[#E1E5E3] text-[#626B73] hover:bg-[#E5F2EF]'
              }`}
            >
              {question.correctIndex === i ? '✓' : String.fromCharCode(65 + i)}
            </button>
            <input
              type="text"
              value={opt}
              onChange={e => {
                const newOpts = [...question.options];
                newOpts[i] = e.target.value;
                onChange({ ...question, options: newOpts });
              }}
              className="min-w-0 flex-1 bg-white border border-[#E1E5E3] rounded px-3 py-1.5 text-sm"
              placeholder={`Вариант ${String.fromCharCode(65 + i)}`}
            />
            {question.options.length > 2 && (
              <button
                onClick={() => {
                  const newOpts = question.options.filter((_, idx) => idx !== i);
                  const newCorrect = question.correctIndex >= i && question.correctIndex > 0
                    ? question.correctIndex - 1
                    : question.correctIndex;
                  onChange({ ...question, options: newOpts, correctIndex: Math.min(newCorrect, newOpts.length - 1) });
                }}
                className="text-[#DC2626] text-xs"
              >✕</button>
            )}
          </div>
        ))}
        {question.options.length < 6 && (
          <button
            onClick={() => onChange({ ...question, options: [...question.options, ''] })}
            className="text-[#0F766E] text-xs hover:text-[#0B5F59]"
          >+ Добавить вариант</button>
        )}
      </div>}

      <textarea
        value={question.explanation || ''}
        onChange={e => onChange({ ...question, explanation: e.target.value })}
        className="w-full bg-white border border-[#E1E5E3] rounded px-3 py-1.5 text-sm h-12"
        placeholder="Пояснение к ответу (необязательно)..."
      />
    </div>
  );
}

export default function QuizEditor({ quizId, onClose }: QuizEditorProps) {
  const [selectedQuestion, setSelectedQuestion] = useState(0);
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getQuiz(quizId)
      .then(setQuiz)
      .catch(err => {
        console.error('Failed to load quiz:', err);
        window.dispatchEvent(new CustomEvent('app-toast', { detail: { message: 'Не удалось загрузить квиз', type: 'error' } }));
        onClose();
      })
      .finally(() => setLoading(false));
  }, [quizId]);

  const updateQuestion = (index: number, q: QuizQuestion) => {
    if (!quiz) return;
    const questions = [...quiz.questions];
    questions[index] = q;
    setQuiz({ ...quiz, questions });
  };

  const deleteQuestion = (index: number) => {
    if (!quiz || quiz.questions.length <= 1) return;
    const questions = quiz.questions.filter((_, i) => i !== index);
    setQuiz({ ...quiz, questions });
  };

  const addQuestion = () => {
    if (!quiz) return;
    const newQ: QuizQuestion = {
      id: `${quiz.id}_q${quiz.questions.length + 1}`,
      text: '',
      options: ['', '', '', ''],
      correctIndex: 0,
      explanation: '',
      difficulty: 'medium',
    };
    setQuiz({ ...quiz, questions: [...quiz.questions, newQ] });
  };

  const handleSave = async () => {
    if (!quiz) return;
    setSaving(true);
    try {
      await updateQuiz(quiz.id, quiz);
      onClose();
    } catch (err) {
      window.dispatchEvent(new CustomEvent('app-toast', { detail: { message: 'Ошибка сохранения квиза', type: 'error' } }));
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#F7F7F4]/95 flex items-center justify-center z-[60]">
        <div className="bg-white border border-[#E1E5E3] rounded-lg p-8">
          <div className="w-8 h-8 border-4 border-[#0F766E] border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  if (!quiz) return null;
  const selectedIndex = Math.min(selectedQuestion, Math.max(0, quiz.questions.length - 1));
  const questionTypes: Record<string, string> = { single: 'Выбор ответа', number: 'Число', free: 'Свободный ответ', hotspot: 'Клик по фото', labels: 'Подписи на фото', line: 'Выбор линии', categories: 'Категории', order: 'Порядок' };

  return (
    <div className="fixed inset-0 bg-[#F7F7F4]/95 flex items-center justify-center z-[60] p-2 sm:p-4">
      <AdminShell modal title="Редактор теста" onBack={onClose} actions={
        <button onClick={handleSave} disabled={saving} className="rounded-lg bg-[#0F766E] px-3 py-2 text-sm text-white hover:bg-[#0B5F59] disabled:opacity-50">
          {saving ? 'Сохранение...' : 'Сохранить'}
        </button>
      }>
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="min-w-0 flex-1">
              <label className="block text-sm text-[#626B73] mb-1">Тема</label>
              <input
                type="text"
                value={quiz.topic}
                onChange={e => setQuiz({ ...quiz, topic: e.target.value })}
                className="w-full bg-white border border-[#E1E5E3] rounded px-3 py-2 text-sm"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-sm text-[#626B73] mb-1">Категория</label>
              <input
                type="text"
                value={quiz.category}
                onChange={e => setQuiz({ ...quiz, category: e.target.value })}
                className="w-full bg-white border border-[#E1E5E3] rounded px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div className="border-t border-[#E1E5E3] pt-3">
            <div className="flex flex-wrap gap-2 items-center justify-between mb-3">
              <span className="text-sm text-[#626B73]">Вопросы ({quiz.questions.length})</span>
              <button
                onClick={() => { addQuestion(); setSelectedQuestion(quiz.questions.length); }}
                className="px-3 py-1 bg-[#0F766E] text-white rounded text-sm hover:bg-[#0B5F59]"
              >+ Добавить вопрос</button>
            </div>

            <div className="grid items-start gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
              <nav aria-label="Вопросы теста" className="max-h-48 overflow-y-auto rounded-lg border border-[#E1E5E3] p-2 lg:sticky lg:top-28 lg:max-h-[calc(94dvh-10rem)]">
                {quiz.questions.map((q, i) => <button key={q.id || i} type="button" onClick={() => setSelectedQuestion(i)} aria-current={selectedIndex === i ? 'true' : undefined}
                  className={`mb-1 block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-[#E5F2EF] ${selectedIndex === i ? 'bg-[#E5F2EF] text-[#0F766E]' : ''}`}>
                  <span className="block font-medium">{i + 1}. {questionTypes[q.type || 'single'] || q.type}</span>
                  <span className="block truncate text-xs text-[#626B73]">{q.text || 'Новый вопрос'}</span>
                </button>)}
              </nav>
              <div className="min-w-0">
                {/* Скрываем без размонтирования: черновики зон сохраняются. */}
                {quiz.questions.map((q, i) => (
                  <div key={q.id || i} hidden={selectedIndex !== i}>
                    <QuestionEditor
                      question={q}
                      index={i}
                      courseId={(quiz as any).collectionId || quiz.category || 'common'}
                      onChange={updated => updateQuestion(i, updated)}
                      onDelete={() => deleteQuestion(i)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

      </AdminShell>
    </div>
  );
}
